import { prefersReducedMotion } from '@/nova/hooks/useReducedMotion';
import { SORTABLE_ANIM } from '@/nova/theme/tokens';

/** How far the copy of a game sent off screen drifts while it fades. */
const SEND_AWAY_PX = 96;

/**
 * A game moved from its menu to a place that is not on screen: a still copy leaves its old spot in the
 * direction the game went, fading as it goes, while the games around it close the gap. The page does not
 * scroll. The copy is inert and hidden from assistive tech, sits in the game's positioned container (so it
 * passes under the pinned filter, not over it), and removes itself when done.
 */
export const sendAway = (el: HTMLElement, direction: 'up' | 'down'): void => {
  const host = el.offsetParent;
  if (prefersReducedMotion() || !(host instanceof HTMLElement)) return;
  const rect = el.getBoundingClientRect();
  const hostRect = host.getBoundingClientRect();
  const ghost = el.cloneNode(true) as HTMLElement;
  // The menu that sent it is still open in this frame; the copy leaves without it.
  ghost.querySelectorAll('[role="menu"]').forEach((node) => node.remove());
  ghost.removeAttribute('data-id');
  ghost.setAttribute('aria-hidden', 'true');
  ghost.setAttribute('inert', '');
  Object.assign(ghost.style, {
    position: 'absolute',
    left: `${rect.left - hostRect.left - host.clientLeft}px`,
    top: `${rect.top - hostRect.top - host.clientTop}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    margin: '0',
    transform: 'none',
    opacity: window.getComputedStyle(el).opacity,
    pointerEvents: 'none',
    zIndex: '4',
  });
  host.appendChild(ghost);
  const animation = ghost.animate(
    [
      { transform: 'translateY(0)' },
      { transform: `translateY(${direction === 'up' ? -SEND_AWAY_PX : SEND_AWAY_PX}px)`, opacity: 0 },
    ],
    { duration: SORTABLE_ANIM.SEND_AWAY_MS, easing: SORTABLE_ANIM.EASE, fill: 'forwards' },
  );
  const remove = (): void => ghost.remove();
  animation.onfinish = remove;
  animation.oncancel = remove;
};
